import { CommonModule, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  AppointmentSlotDto,
  AppointmentSlotStatus,
  OpeningHoursDto,
  PropertyDto,
} from '@workspace/data-types';
import { DialogLayoutAuraComponent } from '@workspace/ng-aura';
import { DateTime } from 'luxon';

registerLocaleData(localeFr);

export interface PropertyVisitInvitationDialogData {
  property: PropertyDto;
  agentName?: string;
  openingHours?: OpeningHoursDto | null;
  excludedSlots?: AppointmentSlotDto[];
}

export interface VisitSlotDraft {
  date: DateTime | null;
  time: string;
  durationMinutes: number;
}

export interface VisitInvitationResult {
  proposedSlots: AppointmentSlotDto[];
  notes?: string;
}

type WeekDayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

const LUXON_WEEKDAY_MAP: Record<number, WeekDayKey> = {
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
  7: 'sunday',
};

const JS_WEEKDAY_MAP: Record<number, WeekDayKey> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

const OPENING_HOURS_KEYS: WeekDayKey[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

@Component({
  selector: 'etxe-property-visit-invitation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    DialogLayoutAuraComponent,
  ],
  templateUrl: './property-visit-invitation-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyVisitInvitationDialogComponent {
  readonly dialogRef = inject(
    MatDialogRef<PropertyVisitInvitationDialogComponent, VisitInvitationResult | undefined>,
  );
  readonly data = inject<PropertyVisitInvitationDialogData>(MAT_DIALOG_DATA);

  // Règle métier : minimum J+1 (24 heures d'anticipation)
  readonly minDate: DateTime = DateTime.now().plus({ days: 1 }).startOf('day');

  // Maximum 60 jours dans le futur
  readonly maxDate: DateTime = DateTime.now().plus({ days: 60 }).endOf('day');

  readonly slots = signal<[VisitSlotDraft, VisitSlotDraft, VisitSlotDraft]>([
    { date: null, time: '10:00', durationMinutes: 45 },
    { date: null, time: '14:30', durationMinutes: 45 },
    { date: null, time: '16:30', durationMinutes: 45 },
  ]);

  readonly notes = signal<string>('');

  readonly hasOpeningHours = computed<boolean>(() => {
    const hours = this.data.openingHours;
    if (!hours) return false;
    return OPENING_HOURS_KEYS.some((day) => Boolean(hours[day]?.isOpen));
  });

  // Filtre désactivant les jours où l'agent ne travaille pas
  // Compatible Luxon DateTime (fourni par LuxonDateAdapter) et JS Date
  readonly dateFilter = (date: DateTime | Date | null): boolean => {
    if (!date) return false;
    const hours = this.data.openingHours;
    if (!hours) return true;

    let dayKey: WeekDayKey | undefined;
    if (DateTime.isDateTime(date)) {
      if (!date.isValid) return false;
      dayKey = LUXON_WEEKDAY_MAP[date.weekday];
    } else if (date instanceof Date) {
      if (Number.isNaN(date.getTime())) return false;
      dayKey = JS_WEEKDAY_MAP[date.getDay()];
    }

    if (!dayKey) return true;

    const dayConfig = hours[dayKey];
    if (dayConfig && dayConfig.isOpen === false) {
      return false;
    }
    return true;
  };

  // Créneaux horaires de sélection
  readonly availableTimeSlots: string[] = [
    '08:30',
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
    '17:00',
    '17:30',
    '18:00',
    '18:30',
    '19:00',
  ];

  readonly durationOptions = [
    { label: '30 minutes', value: 30 },
    { label: '45 minutes', value: 45 },
    { label: '1 heure', value: 60 },
  ];

  readonly isValid = computed<boolean>(() => {
    const currentSlots = this.slots();
    const allFilled = currentSlots.every((s) => s.date !== null && s.date.isValid && Boolean(s.time));
    if (!allFilled) return false;

    // Vérifier l'unicité des créneaux
    const timestamps = currentSlots.map((s) => {
      const date = s.date;
      if (!date) return 0;
      const [h, m] = s.time.split(':').map(Number);
      return date.set({ hour: h, minute: m, second: 0, millisecond: 0 }).toMillis();
    });

    const uniqueTimestamps = new Set(timestamps);
    if (uniqueTimestamps.size !== 3) return false;

    // Vérifier qu'aucun créneau ne correspond à un créneau déjà refusé par l'agent
    const hasExcludedConflict = currentSlots.some((s) => this.isSlotExcluded(s));
    return !hasExcludedConflict;
  });

  isSlotExcluded(slot: VisitSlotDraft): boolean {
    if (!slot.date || !slot.time || !this.data.excludedSlots?.length) return false;
    const [h, m] = slot.time.split(':').map(Number);
    const slotStart = slot.date.set({ hour: h, minute: m, second: 0, millisecond: 0 });
    return this.data.excludedSlots.some((exc) => {
      const excDate = DateTime.fromISO(exc.startAt);
      return (
        slotStart.hasSame(excDate, 'day') &&
        slotStart.hour === excDate.hour &&
        slotStart.minute === excDate.minute
      );
    });
  }

  updateSlotDate(index: 0 | 1 | 2, date: DateTime | Date | null): void {
    let dt: DateTime | null = null;
    if (DateTime.isDateTime(date)) {
      dt = date.isValid ? date : null;
    } else if (date instanceof Date) {
      const parsed = DateTime.fromJSDate(date);
      dt = parsed.isValid ? parsed : null;
    }

    this.slots.update((current) => {
      const next: [VisitSlotDraft, VisitSlotDraft, VisitSlotDraft] = [...current];
      next[index] = { ...next[index], date: dt };
      return next;
    });
  }

  updateSlotTime(index: 0 | 1 | 2, time: string): void {
    this.slots.update((current) => {
      const next: [VisitSlotDraft, VisitSlotDraft, VisitSlotDraft] = [...current];
      next[index] = { ...next[index], time };
      return next;
    });
  }

  updateSlotDuration(index: 0 | 1 | 2, durationMinutes: number): void {
    this.slots.update((current) => {
      const next: [VisitSlotDraft, VisitSlotDraft, VisitSlotDraft] = [...current];
      next[index] = { ...next[index], durationMinutes };
      return next;
    });
  }

  onSubmit(): void {
    if (!this.isValid()) return;

    const currentSlots = this.slots();
    const proposedSlots: AppointmentSlotDto[] = currentSlots.map((s) => {
      const [hours, minutes] = s.time.split(':').map(Number);
      const date = s.date ?? DateTime.now();
      const start = date.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
      const end = start.plus({ minutes: s.durationMinutes });

      return {
        startAt: start.toJSDate().toISOString(),
        endAt: end.toJSDate().toISOString(),
        status: AppointmentSlotStatus.PROPOSED,
      };
    });

    this.dialogRef.close({
      proposedSlots,
      notes: this.notes().trim() || undefined,
    });
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }
}
